import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TrackerCatalyst, TrackerPermissions } from '../../types';
import { groupByMonth, today } from './helpers';
import CatalystCard from './CatalystCard';

interface Props {
  rows: TrackerCatalyst[];
  /** The full (unfiltered) catalyst list — read-through/Perspective scoring cross-references against
   * every tracked catalyst, not just the ones the current filters happen to show. */
  allRows: TrackerCatalyst[];
  watchedIds: Set<string>;
  onToggleFavorite: (id: string) => void;
  permissions: TrackerPermissions | null;
  onSuggestCorrection: (row: TrackerCatalyst) => void;
  readMap: Record<string, string> | undefined;
  onToggleRead: (id: string, on: boolean) => void;
  perspectiveCompany?: string;
  onOpenCatalyst: (id: string) => void;
}

/** The month-rail + month-card "ledger" from tracker.js's renderList(). */
const CatalystListView: React.FC<Props> = ({ rows, allRows, watchedIds, onToggleFavorite, permissions, onSuggestCorrection, readMap, onToggleRead, perspectiveCompany, onOpenCatalyst }) => {
  const groups = useMemo(() => groupByMonth(rows), [rows]);
  const railRef = useRef<HTMLElement>(null);
  const [currentKey, setCurrentKey] = useState<string | null>(null);

  // tracker.js's updateRail(): highlight whichever month section is in view as the page scrolls, rather
  // than leaving the rail static — the dot/bold text track scroll position, not just a click.
  useEffect(() => {
    const update = () => {
      const ctrlH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ctrl-h')) || 0;
      const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 0;
      const off = navH + ctrlH + 20;
      let cur: string | null = null;
      for (const el of Array.from(document.querySelectorAll<HTMLElement>('.month'))) {
        if (el.getBoundingClientRect().bottom > off) { cur = el.dataset.m || null; break; }
      }
      setCurrentKey(cur);
    };
    update();
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [groups]);

  // Keep the active rail button scrolled into view within the (independently scrolling) rail itself.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !currentKey) return;
    const btn = rail.querySelector<HTMLElement>(`button[data-jump="${currentKey}"]`);
    if (!btn) return;
    const rr = rail.getBoundingClientRect(), br = btn.getBoundingClientRect();
    if (br.top < rr.top || br.bottom > rr.bottom) rail.scrollTop += br.top - rr.top - 40;
  }, [currentKey]);

  if (!rows.length) {
    return (
      <div className="empty">
        <b>Nothing here</b>
        No catalysts match the current filters.
      </div>
    );
  }

  const TODAY_MONTH = today().slice(0, 7);
  let lastYear = '';

  const jumpTo = (key: string) => {
    document.getElementById(`m-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ledger">
      <nav className="rail" aria-label="Months" ref={railRef}>
        {groups.map((g) => {
          const year = g.key.slice(0, 4);
          const showYear = g.key !== 'tbd' && year !== lastYear;
          lastYear = g.key === 'tbd' ? lastYear : year;
          const monthLabel = g.key === 'tbd' ? 'Not guided' : g.label.split(' ')[0];
          const cls = g.key === 'tbd' ? 'tbd' : g.key < TODAY_MONTH ? 'past' : g.key === TODAY_MONTH ? 'now' : '';
          return (
            <React.Fragment key={g.key}>
              {showYear && <div className="ry" aria-hidden="true">{year}</div>}
              <button
                type="button"
                className={cls || undefined}
                data-jump={g.key}
                aria-current={currentKey === g.key}
                aria-label={g.key === 'tbd' ? 'Timing not guided' : g.label}
                onClick={() => jumpTo(g.key)}
              >
                <span className="dot" aria-hidden="true" /><span className="mm">{monthLabel}</span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>
      <div className="months">
        {groups.map((g) => (
          <section className="month" id={`m-${g.key}`} data-m={g.key} key={g.key} aria-label={g.label}>
            {g.rows.map((row) => (
              <CatalystCard
                key={row.id}
                row={row}
                allRows={allRows}
                perspectiveCompany={perspectiveCompany}
                isWatched={watchedIds.has(row.id)}
                onToggleFavorite={onToggleFavorite}
                permissions={permissions}
                onSuggestCorrection={onSuggestCorrection}
                readMap={readMap}
                onToggleRead={onToggleRead}
                onOpenCatalyst={onOpenCatalyst}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default CatalystListView;
